import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import {
  ENTERPRISE_MEMBER_INVITATION_STATUS,
  EnterpriseMember,
  EnterpriseMemberInvitationStatus,
} from 'models/Enterprises';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  CUSTOM_COL_SIZES,
  HEADING_TRANSLATIONS,
  SORT_COLS,
  TABLE_COLUMNS,
  EnterpriseTeamMembersTable,
  RWC_TABLE_COLUMNS,
} from './components/TeamMembersTableCells/data';
import { Checkbox } from 'ncoded-component-library';
import { BODY_CONTENT_GENERATE } from './components/TeamMembersTableCells';
import { RightClickDropdown } from 'components/RightClickDropdown';
import EnterpriseMembersContext from 'providers/EnterpriseMembers/EnterpriseMembers.context';

import './TeamMembersTable.styles.scss';

const NOT_ACCEPTED = [
  ENTERPRISE_MEMBER_INVITATION_STATUS.INVITATION_SENT,
  ENTERPRISE_MEMBER_INVITATION_STATUS.NOT_INVITED,
] as EnterpriseMemberInvitationStatus[];

const COLSPAN: Record<string, number> = {
  totalDeals: 6,
  totalDataRooms: 3,
};

type TeamMembersTableProps = {
  className?: string;
  isRWC?: boolean;
};

const TeamMembersTable: React.FC<TeamMembersTableProps> = (props) => {
  const { className, isRWC = false } = props;

  const classes = classNames('team-members-table', className);

  const {
    members,
    selected,
    totalPages,
    isLoading,
    isAllSelected,
    getDropdownOptions,
    onSelectAll,
    checkIfMemberIsSelected,
    updateSelected,
  } = useContext(EnterpriseMembersContext);

  const headerColTransformation = useMemo(
    () => ({
      checkbox: () => (
        <Checkbox checked={isAllSelected} onChange={onSelectAll} />
      ),
    }),
    [isAllSelected, onSelectAll],
  );

  const columns = useMemo(
    () => (isRWC ? RWC_TABLE_COLUMNS : TABLE_COLUMNS),
    [isRWC],
  );

  const renderedContent = useMemo(
    () =>
      columns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (member: EnterpriseMember) => (
            <RightClickDropdown
              options={getDropdownOptions(
                selected?.length ? selected : [member],
              )}
            >
              {BODY_CONTENT_GENERATE[col]({
                member,
              })}
            </RightClickDropdown>
          ),
        }),
        {},
      ),
    [selected, columns, getDropdownOptions],
  );

  return (
    <ReorderResizeTable
      className={classes}
      columns={columns}
      data={members as unknown as EnterpriseTeamMembersTable[]}
      isDragDisabled
      isResizeDisabled
      isDataLoading={isLoading}
      totalPages={totalPages}
      customSizes={CUSTOM_COL_SIZES}
      headingTranslations={HEADING_TRANSLATIONS}
      sortingValues={SORT_COLS}
      tableHeaderContentTransformations={headerColTransformation}
      tableContentTransformations={renderedContent}
      getIsRowSelected={(member) =>
        checkIfMemberIsSelected(member as unknown as EnterpriseMember)
      }
      onRowClick={(member) =>
        updateSelected(member as unknown as EnterpriseMember)
      }
      getColSpan={({ invitationStatus }, col) =>
        NOT_ACCEPTED.includes(invitationStatus) && COLSPAN[col]
      }
    />
  );
};

export default TeamMembersTable;
